import React, { useState } from 'react';
import { Box, Typography, Button, TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Stack, Grid, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, Divider } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import PrintIcon from '@mui/icons-material/Print';
import axios from 'axios';

const formatDateForDisplay = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? '' : date.toLocaleDateString('en-ZA');
};

export default function DebtorsTab({ formData, handleFieldChange, isEditingMode, user, matterId }) {
    const debtors = formData?.debtorsList || [];
    const isNPAMatter = formData?.estateClassification === 'NPA' || formData?.estateClassification === 'Npa Matters' || formData?.estateClassification === 'Curator Appointments';

    const handlePrintDebtors = () => {
        window.open(`/api/debtors/download-pdf?matterId=${matterId}`, '_blank');
    };

    // State for the currently edited/new debtor
    const [editingDebtorId, setEditingDebtorId] = useState(null);
    const [debtorForm, setDebtorForm] = useState({
        debtorType: 'Individual',
        debtorName: '',
        surname: '',
        contactNumber: '',
        emailAddress: '',
        postalAddress: '',
        physicalAddress: '',
        referenceNumber: '',
        registrationNumber: '', // New field for Entity Debtors
        contactPerson: '',     // New field for Entity Debtors
        amountOwed: '',
        amountCollected: '',
        payments: [], // New field for payment history
        status: 'Outstanding',
        irrecoverableReason: '',
        notes: ''
    });

    // State for the payment management dialog
    const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
    const [selectedDebtorForPayments, setSelectedDebtorForPayments] = useState(null);
    const [paymentForm, setPaymentForm] = useState({ paymentDate: '', paymentAmount: '' });



    const resetForm = () => {
        setEditingDebtorId(null);
        setDebtorForm({
            debtorName: '',
            debtorType: 'Individual',
            surname: '',
            contactNumber: '',
            emailAddress: '',
            postalAddress: '',
            physicalAddress: '',
            referenceNumber: '',
            registrationNumber: '',
            contactPerson: '',
            amountOwed: '',
            amountCollected: '',
            payments: [],
            status: 'Outstanding',
            notes: '',
            irrecoverableReason: ''
        });
    };

    const saveToDB = async (list) => {
        if (!matterId) return;
        try {
            await axios.put(`/api/legal-matter/${matterId}`, { debtorsList: list });
        } catch (e) {
            console.error("Auto-save failed", e);
        }
    };

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        if (name === 'amountOwed') {
            // Allow only numbers and a single decimal point, then format with commas
            const numericValue = value.replace(/[^0-9.]/g, '');
            const parts = numericValue.split('.');
            parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
            const formattedValue = parts.join('.');
            setDebtorForm(prev => ({ ...prev, [name]: formattedValue }));
        } else if (name === 'paymentAmount') {
            // This will be handled in the payment form's onChange directly
            setPaymentForm(prev => ({ ...prev, [name]: value }));
        } else {
            setDebtorForm(prev => ({ ...prev, [name]: value }));
        }
    };

    // Auto-update status based on payments
    const updateDebtorStatus = (debtor) => {
        const owed = parseFloat(debtor.amountOwed) || 0;
        const collected = (debtor.payments || []).reduce((sum, p) => sum + (parseFloat(p.paymentAmount) || 0), 0);
        
        // If status is manually set to 'Written Off' or 'Collected', respect it.
        if (debtor.status === 'Written Off' || debtor.status === 'Collected') {
            return debtor.status;
        }
        // Otherwise, calculate it based on amounts.
        if (owed > 0 && collected >= owed) {
            return 'Collected';
        }
        return 'Outstanding';
    };

    const handleAddPayment = () => {
        if (!paymentForm.paymentDate || !paymentForm.paymentAmount || !selectedDebtorForPayments) return;

        const newPayment = {
            id: -Math.floor(Math.random() * 1000000), // Negative ID for new client-side items
            paymentDate: paymentForm.paymentDate,
            // Save the unformatted numeric value
            paymentAmount: paymentForm.paymentAmount.replace(/,/g, '') 
        };

        let newlyUpdatedDebtor = null;

        const updatedDebtors = debtors.map(d => {
            if (d.id === selectedDebtorForPayments?.id) {
                const updatedPayments = [...(d.payments || []), newPayment];
                newlyUpdatedDebtor = { ...d, payments: updatedPayments, status: updateDebtorStatus({ ...d, payments: updatedPayments }) };
                return newlyUpdatedDebtor;
            }
            return d;
        });

        saveToDB(updatedDebtors);
        handleFieldChange({ target: { name: 'debtorsList', value: updatedDebtors, type: 'array' } });
        setPaymentForm({ paymentDate: '', paymentAmount: '' }); // Reset payment form
        if (newlyUpdatedDebtor) setSelectedDebtorForPayments(newlyUpdatedDebtor); // Refresh the dialog's view
    };

    const handleSaveDebtor = () => {
        if (!debtorForm.debtorName) return;
        
        let updatedList = [...debtors];
        // Unformat the amount before saving
        const debtorDataToSave = { 
            ...debtorForm, 
            amountOwed: String(debtorForm.amountOwed).replace(/,/g, ''),
            amountCollected: String(debtorForm.amountCollected || '').replace(/,/g, '')
        };

        if (editingDebtorId) {
            updatedList = updatedList.map(d => d.id === editingDebtorId ? { ...debtorDataToSave, id: editingDebtorId } : d);
        } else {
            const newDebtor = { ...debtorForm, id: -Math.floor(Math.random() * 1000000), payments: [] };
            updatedList.push(newDebtor);
        }

        updatedList = updatedList.map(d => ({ ...d, status: updateDebtorStatus(d) }));

        handleFieldChange({ target: { name: 'debtorsList', value: updatedList, type: 'array' } });
        saveToDB(updatedList);
        resetForm();
    };

    const handleEditDebtor = (debtor) => {
        setEditingDebtorId(debtor.id);
        // Format the amount for display in the form
        const amountOwed = parseFloat(debtor.amountOwed || 0);
        const formattedAmount = isNaN(amountOwed) ? '' : amountOwed.toLocaleString('en-US');
        setDebtorForm({ ...debtor, amountOwed: formattedAmount });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleDeleteDebtor = (id) => {
        if (window.confirm('Are you sure you want to delete this debtor?')) {
            const updatedList = debtors.filter(d => d.id !== id);
            handleFieldChange({ target: { name: 'debtorsList', value: updatedList, type: 'array' } });
            saveToDB(updatedList);
        }
    };

    const handleOpenPaymentDialog = (debtor) => {
        setSelectedDebtorForPayments(debtor);
        setPaymentDialogOpen(true);
        setPaymentForm({ paymentDate: '', paymentAmount: '' });
    };

    const isDisabled = !isEditingMode || user?.role === 'guest';

    // Color coding logic based on requirements
    const getRowStyle = (debtor) => {
        if (debtor.status === 'Written Off') return { backgroundColor: '#f5f5f5', color: 'text.secondary' }; // Grey tint
        
        const owed = parseFloat(debtor.amountOwed || 0);
        const collected = (debtor.payments || []).reduce((sum, p) => sum + (parseFloat(p.paymentAmount) || 0), 0);
        const outstanding = owed - collected;

        if (outstanding <= 0 || debtor.status === 'Collected') {
            return { backgroundColor: '#e8f5e9' }; // Green tint (Paid in full)
        }
        
        if (outstanding > 0) {
            return { backgroundColor: '#ffebee' }; // Red tint (Amount is outstanding)
        }

        return {};
    };

    // Calculate Totals
    const totalOwed = debtors.reduce((sum, d) => sum + parseFloat(d.amountOwed || 0), 0);
    const totalCollected = debtors.reduce((debtorSum, d) => 
        debtorSum + (d.payments || []).reduce((paymentSum, p) => paymentSum + parseFloat(p.paymentAmount || 0), 0), 
    0);
    const totalOutstanding = totalOwed - totalCollected;

    return (
        <Box sx={{ p: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    Debtors
                </Typography>
                {!isNPAMatter && (
                    <Button variant="outlined" startIcon={<PrintIcon />} onClick={handlePrintDebtors} disabled={!matterId}>
                        Print / Export Debtors
                    </Button>
                )}
            </Stack>

            {/* 📊 SUMMARY DASHBOARD */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={4}>
                    <Paper elevation={2} sx={{ p: 2, textAlign: 'center', bgcolor: '#e3f2fd' }}>
                        <Typography variant="subtitle2" color="text.secondary">Total Amount Owed</Typography>
                        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>R {totalOwed.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <Paper elevation={2} sx={{ p: 2, textAlign: 'center', bgcolor: '#e8f5e9' }}>
                        <Typography variant="subtitle2" color="text.secondary">Total Amount Collected</Typography>
                        <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'success.dark' }}>R {totalCollected.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <Paper elevation={2} sx={{ p: 2, textAlign: 'center', bgcolor: totalOutstanding > 0 ? '#ffebee' : '#f5f5f5' }}>
                        <Typography variant="subtitle2" color="text.secondary">Total Outstanding Balance</Typography>
                        <Typography variant="h5" sx={{ fontWeight: 'bold', color: totalOutstanding > 0 ? 'error.dark' : 'text.primary' }}>R {totalOutstanding.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
                    </Paper>
                </Grid>
            </Grid>

            {/* � CAPTURE / EDIT FORM */}
            <Paper variant="outlined" sx={{ p: 3, mb: 4, bgcolor: '#fcfcfc', borderTop: `4px solid ${editingDebtorId ? '#ff9800' : '#1976d2'}` }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                    {editingDebtorId ? 'Edit Debtor' : 'Add New Debtor'}
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={3}>
                        <TextField select fullWidth size="small" label="Debtor Type" name="debtorType" value={debtorForm.debtorType} onChange={handleFormChange} disabled={isDisabled}>
                            <MenuItem value="Individual">Individual</MenuItem>
                            <MenuItem value="Entity">Entity (Company/Trust)</MenuItem>
                        </TextField>
                    </Grid>
                    {debtorForm.debtorType === 'Individual' ? (
                        <>
                            <Grid item xs={12} sm={3}>
                                <TextField fullWidth size="small" label="First Name" name="debtorName" value={debtorForm.debtorName} onChange={handleFormChange} disabled={isDisabled} />
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <TextField fullWidth size="small" label="Surname" name="surname" value={debtorForm.surname || ''} onChange={handleFormChange} disabled={isDisabled} />
                            </Grid>
                        </>
                    ) : (
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth size="small" label="Company / Entity Name" name="debtorName" value={debtorForm.debtorName} onChange={handleFormChange} disabled={isDisabled} />
                        </Grid>
                    )}
                    {debtorForm.debtorType === 'Entity' && (
                        <>
                            <Grid item xs={12} sm={3}>
                                <TextField fullWidth size="small" label="Registration Number" name="registrationNumber" value={debtorForm.registrationNumber || ''} onChange={handleFormChange} disabled={isDisabled} />
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <TextField fullWidth size="small" label="Contact Person" name="contactPerson" value={debtorForm.contactPerson || ''} onChange={handleFormChange} disabled={isDisabled} />
                            </Grid>
                        </>
                    )}
                     <Grid item xs={12} sm={3}>
                        <TextField fullWidth size="small" label="Reference Number" name="referenceNumber" value={debtorForm.referenceNumber || ''} onChange={handleFormChange} disabled={isDisabled} />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth size="small" label="Contact Number" name="contactNumber" value={debtorForm.contactNumber} onChange={handleFormChange} disabled={isDisabled} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth size="small" label="Email Address" type="email" name="emailAddress" value={debtorForm.emailAddress} onChange={handleFormChange} disabled={isDisabled} />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth size="small" label="Postal Address" name="postalAddress" value={debtorForm.postalAddress} onChange={handleFormChange} disabled={isDisabled} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth size="small" label="Physical Address" name="physicalAddress" value={debtorForm.physicalAddress || ''} onChange={handleFormChange} disabled={isDisabled} />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth size="small" label="Amount Owed" type="text" name="amountOwed" value={debtorForm.amountOwed} onChange={handleFormChange} disabled={isDisabled} InputProps={{ startAdornment: <Typography sx={{ mr: 1, color: 'text.secondary' }}>R</Typography> }}/>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField select fullWidth size="small" label="Collection Status" name="status" value={debtorForm.status} onChange={handleFormChange} disabled={isDisabled}>
                            <MenuItem value="Outstanding">Outstanding</MenuItem>
                            <MenuItem value="Collected">Collected</MenuItem>
                            <MenuItem value="Written Off">Written Off</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField fullWidth multiline rows={2} size="small" label="General Notes" name="notes" value={debtorForm.notes} onChange={handleFormChange} disabled={isDisabled} />
                    </Grid>

                    {debtorForm.status === 'Written Off' && (
                        <Grid item xs={12}>
                            <TextField fullWidth multiline rows={2} size="small" label="Reason for Write-Off" name="irrecoverableReason" value={debtorForm.irrecoverableReason || ''} onChange={handleFormChange} disabled={isDisabled} sx={{ bgcolor: '#fffde7' }} />
                        </Grid>
                    )}

                    <Grid item xs={12}>
                        <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 1 }}>
                            {editingDebtorId && (
                                <Button variant="outlined" onClick={resetForm} disabled={isDisabled}>Cancel Edit</Button>
                            )}
                            <Button 
                                variant="contained" 
                                color={editingDebtorId ? "warning" : "primary"} 
                                onClick={handleSaveDebtor} 
                                disabled={isDisabled || !debtorForm.debtorName}
                                startIcon={!editingDebtorId && <AddCircleOutlineIcon />}
                            >
                                {editingDebtorId ? 'Update Debtor' : 'Add Debtor'}
                            </Button>
                        </Stack>
                    </Grid>
                </Grid>
            </Paper>

            {/* 📊 SUMMARY TABLE */}
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>Debtors Summary</Typography>
            <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                    <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>Debtors Name</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', textAlign: 'right' }}>Amount Owed</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', textAlign: 'right' }}>Amount Collected</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', textAlign: 'right' }}>Amount Outstanding</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Contact Details</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Notes</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {debtors.map((debtor) => {
                            const owed = parseFloat(debtor.amountOwed || 0);
                            const collected = (debtor.payments || []).reduce((sum, p) => sum + (parseFloat(p.paymentAmount) || 0), 0);
                            const outstanding = owed - collected;

                            return (
                                <TableRow key={debtor.id} sx={getRowStyle(debtor)}>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                            {debtor.debtorType === 'Individual' 
                                                ? `${debtor.debtorName} ${debtor.surname || ''}` 
                                                : (
                                                    <>
                                                        {debtor.debtorName} {debtor.registrationNumber && <Typography variant="caption" display="block" color="text.secondary">Reg No: {debtor.registrationNumber}</Typography>}
                                                    </>)}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">{debtor.status}</Typography>
                                    </TableCell>
                                    <TableCell align="right">R {owed.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2})}</TableCell>
                                    <TableCell align="right">R {collected.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2})}</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>R {outstanding.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2})}</TableCell>
                                    <TableCell>
                                        {debtor.debtorType === 'Entity' && debtor.contactPerson && <Typography variant="caption" display="block">{debtor.contactPerson} (Contact)</Typography>}
                                        <Typography variant="caption" display="block">{debtor.contactNumber}</Typography>
                                        <Typography variant="caption" display="block">{debtor.emailAddress}</Typography>
                                    </TableCell>
                                    <TableCell>{debtor.notes || '-'}</TableCell>
                                    <TableCell align="center">
                                        <Stack direction="row" spacing={1} justifyContent="center">
                                            <IconButton size="small" color="primary" onClick={() => handleEditDebtor(debtor)} disabled={isDisabled}><EditIcon fontSize="small" /></IconButton>
                                            <Button size="small" variant="outlined" sx={{ textTransform: 'none', fontSize: '0.75rem' }} onClick={() => handleOpenPaymentDialog(debtor)} disabled={isDisabled}>Payments</Button>
                                            <IconButton size="small" color="error" onClick={() => handleDeleteDebtor(debtor.id)} disabled={isDisabled}><DeleteIcon fontSize="small" /></IconButton>
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                        
                        {debtors.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary', fontStyle: 'italic' }}>
                                    No debtors recorded yet. Add one using the form above.
                                </TableCell>
                            </TableRow>
                        ) : (
                            // Totals Row
                            <TableRow sx={{ backgroundColor: '#fafafa' }}>
                                <TableCell sx={{ fontWeight: 'bold', textAlign: 'right' }}>Total</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>R {totalOwed.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2})}</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold', color: 'success.main' }}>R {totalCollected.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2})}</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold', color: totalOutstanding > 0 ? 'error.main' : 'text.primary' }}>R {totalOutstanding.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2})}</TableCell>
                                <TableCell colSpan={3}></TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Payment Management Dialog */}
            <Dialog open={paymentDialogOpen} onClose={() => setPaymentDialogOpen(false)} fullWidth maxWidth="md">
                <DialogTitle>Manage Payments for: <strong>{selectedDebtorForPayments?.debtorName}</strong></DialogTitle>
                <DialogContent>
                    {selectedDebtorForPayments && (
                        <>
                            <Grid container spacing={2} sx={{ mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                                <Grid item xs={4}><Typography><strong>Amount Owed:</strong> R {parseFloat(selectedDebtorForPayments.amountOwed || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}</Typography></Grid>
                                <Grid item xs={4}><Typography color="success.main"><strong>Total Collected:</strong> R {(selectedDebtorForPayments.payments || []).reduce((s, p) => s + parseFloat(p.paymentAmount || 0), 0).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}</Typography></Grid>
                                <Grid item xs={4}><Typography color="error.main"><strong>Outstanding:</strong> R {(parseFloat(selectedDebtorForPayments.amountOwed || 0) - (selectedDebtorForPayments.payments || []).reduce((s, p) => s + parseFloat(p.paymentAmount || 0), 0)).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}</Typography></Grid>
                            </Grid>

                            <Divider sx={{ my: 2 }} />

                            <Typography variant="h6" sx={{ mb: 1 }}>Add New Payment</Typography>
                            <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
                                <TextField size="small" type="date" label="Payment Date" name="paymentDate" value={paymentForm.paymentDate} onChange={(e) => setPaymentForm(p => ({...p, paymentDate: e.target.value}))} InputLabelProps={{ shrink: true }} disabled={isDisabled} inputProps={{ max: '9999-12-31' }}/>
                                <TextField 
                                    size="small" 
                                    type="text" 
                                    label="Amount Paid" 
                                    name="paymentAmount" 
                                    value={paymentForm.paymentAmount} 
                                    onChange={(e) => {
                                        const numericValue = e.target.value.replace(/[^0-9.]/g, '');
                                        const parts = numericValue.split('.');
                                        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                                        setPaymentForm(p => ({...p, paymentAmount: parts.join('.')}));
                                    }} 
                                    InputProps={{ startAdornment: <Typography sx={{ mr: 1, color: 'text.secondary' }}>R</Typography> }} disabled={isDisabled} />
                                <Button variant="contained" onClick={handleAddPayment} disabled={isDisabled || !paymentForm.paymentDate || !paymentForm.paymentAmount}>Add Payment</Button>
                            </Stack>

                            <Typography variant="h6" sx={{ mb: 1 }}>Payment History</Typography>
                            <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Payment Date</TableCell>
                                            <TableCell align="right">Amount Paid</TableCell>
                                            <TableCell align="center">Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {(selectedDebtorForPayments.payments || []).length === 0 ? (
                                            <TableRow><TableCell colSpan={3} align="center" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>No payments recorded.</TableCell></TableRow>
                                        ) : (
                                            [...selectedDebtorForPayments.payments].sort((a, b) => new Date(b.paymentDate) - new Date(a.paymentDate)).map(p => (
                                                <TableRow key={p.id}>
                                                    <TableCell>{formatDateForDisplay(p.paymentDate)}</TableCell>
                                                    <TableCell align="right">R {parseFloat(p.paymentAmount || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}</TableCell>
                                                    <TableCell align="center">
                                                        <IconButton size="small" color="error" onClick={() => {
                                                            if (window.confirm('Delete this payment?')) {
                                                                const updatedDebtors = debtors.map(d => {
                                                                    if (d.id === selectedDebtorForPayments.id) {
                                                                        const updatedPayments = d.payments.filter(pay => pay.id !== p.id);
                                                                        return { ...d, payments: updatedPayments, status: updateDebtorStatus({ ...d, payments: updatedPayments }) };
                                                                    }
                                                                    return d;
                                                                });
                                                                saveToDB(updatedDebtors);
                                                                handleFieldChange({ target: { name: 'debtorsList', value: updatedDebtors, type: 'array' } });
                                                            }
                                                        }} disabled={isDisabled}><DeleteIcon fontSize="inherit" /></IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </>
                    )}
                </DialogContent>
                <DialogActions><Button onClick={() => setPaymentDialogOpen(false)}>Close</Button></DialogActions>
            </Dialog>
        </Box>
    );
}